import {
  ModalConfirmV2,
  ModalSizeVariants,
  SwitchV2,
  SwitchVarient,
  TextAreaWithChips,
} from '@labrav/react-components'
import { Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from '../../../store'
import {
  CloudMarketplace,
  Product,
} from '../../../modules/productsListing/types'
import {
  enableConcurrentForBuyer,
  getProducts,
} from '../../../modules/productsListing/action'
import { awsAccountIdRegex } from '../../../common/utils/constants'
import { useStyles } from './EnableConcurrent.styles'

const IDS_REQUIRED_ERROR = 'At least one ID is required.'
const INVALID_IDS_ERROR = 'Enter valid IDs.'

const isValidAccountId = (id: string) => awsAccountIdRegex.test(id.trim())

const parseAccountIdsFromInput = (input: string): string[] => {
  const ids = input.includes(',') ? input.split(',') : [input]
  return ids.map(id => id.trim()).filter(Boolean)
}

export type EnableConcurrentProps = {
  enableConcurrentModal: Product
  handleClose: () => void
  accountId: string
  cloudMarketplace: CloudMarketplace
}

export const EnableConcurrent = ({
  enableConcurrentModal,
  handleClose,
  accountId,
  cloudMarketplace,
}: EnableConcurrentProps) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const [accountIds, setAccountIds] = useState<string[]>([])
  const [idsError, setIdsError] = useState('')

  useEffect(() => {
    setAccountIds([])
    setIdsError('')
  }, [enableConcurrentModal.productId])

  const handleTagsChange = useCallback((newTag: string) => {
    if (!newTag?.trim()) {
      return
    }

    const idsToAdd = parseAccountIdsFromInput(newTag)
    const hasInvalidId = idsToAdd.some(id => !isValidAccountId(id))

    if (hasInvalidId) {
      setIdsError(INVALID_IDS_ERROR)
      return
    }

    setAccountIds(previousIds => {
      const uniqueNewIds = idsToAdd.filter(id => !previousIds.includes(id))
      return uniqueNewIds.length > 0
        ? [...previousIds, ...uniqueNewIds]
        : previousIds
    })
    setIdsError('')
  }, [])

  const handleDelete = useCallback((value: string) => {
    setAccountIds(previousIds => previousIds.filter(id => id !== value))
    setIdsError('')
  }, [])

  const handleEnableConcurrentUsage = async () => {
    if (accountIds.length === 0) {
      setIdsError(IDS_REQUIRED_ERROR)
      return
    }

    if (!accountIds.every(isValidAccountId)) {
      setIdsError(INVALID_IDS_ERROR)
      return
    }

    setIdsError('')

    await dispatch(
      enableConcurrentForBuyer({
        partnerId: accountId,
        productId: enableConcurrentModal.productId,
        accountIds,
        callBack: async (data: { success: boolean }) => {
          if (data.success) {
            handleClose()
            await dispatch(getProducts(accountId, cloudMarketplace))
          }
        },
      })
    )
  }

  return (
    <ModalConfirmV2
      title={'Enable concurrent for buyer'}
      subTitle={''}
      open={true}
      content={
        <div
          className={classes.content}
          data-testid="enable-concurrent-modal-content"
        >
          <Typography className={classes.description}>
            Enter account IDs to enable concurrent for buyer for this product.
            Press enter or comma to add each ID.
          </Typography>
          <div className={classes.textFieldWidth}>
            <TextAreaWithChips
              id="enable-concurrent-account-ids"
              label="Enter comma-separated account IDs"
              onChange={handleTagsChange}
              handleDelete={handleDelete}
              selectedValues={accountIds}
              type="number"
              showSpinner={false}
              error={Boolean(idsError)}
              helperText={idsError}
              data-testid="enable-concurrent-ids-input"
            />
          </div>
        </div>
      }
      titleAcceptButton={'Submit'}
      onAccept={handleEnableConcurrentUsage}
      onDecline={handleClose}
      variant={ModalSizeVariants.large}
      acceptDisabled={false}
    />
  )
}

export const HardOptEnableConcurrent = ({
  productRow,
  currentAccountId,
  selectedCloudTypeProductListing,
}: {
  productRow: Product
  currentAccountId: string
  selectedCloudTypeProductListing: CloudMarketplace
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const [checked, setChecked] = useState(!!productRow?.isConcurrentEnabled)

  const handleEnableConcurrentUsageHardOpt = async () => {
    await setChecked(true)
    await dispatch(
      enableConcurrentForBuyer({
        partnerId: currentAccountId,
        productId: productRow.productId,
        hardOpt: true,
        callBack: async (data: { success: boolean }) => {
          if (data.success) {
            await dispatch(
              getProducts(currentAccountId, selectedCloudTypeProductListing)
            )
          } else {
            await setChecked(false)
          }
        },
      })
    )
  }

  return (
    <div className={classes.switchContainer}>
      <SwitchV2
        switchVarient={SwitchVarient.DARK}
        checked={checked}
        onChange={handleEnableConcurrentUsageHardOpt}
        disabled={productRow?.isConcurrentEnabled}
      />
      <Typography className={classes.switchContainer}>Hard Opt</Typography>
    </div>
  )
}
